import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Typography,
} from "@material-ui/core";
import { sidebarStyles } from "./SidebarContent";
import type { UserState } from "../../context/auth";
import { useColumnVisibility } from "../../helpers/useColumnVisibility";
import { useMemo, useState } from "react";
import { moduleIdMap, soilKpis, substrateKpis } from "../../constants/kpis";
import { deduplicate } from "../../helpers/deduplication";
import { useMetricsState } from "../../context/metrics";
import FormButton from "../forms/FormButton";
import { useFieldsState } from "../../context/fields";

const DashboardColumnSettings = ({ user }: { user: UserState }) => {
  const classes = sidebarStyles();
  const metrics = useMetricsState();
  const { fields } = useFieldsState();

  const { isVisible, toggleColumns } = useColumnVisibility(user);

  const columns = useMemo<{ id: string; label: string }[]>(() => {
    const kpis: string[] = [];
    const availableModules = new Set(
      user.client.module_access?.map((m) => moduleIdMap.getBack(m.module_id))
    );
    if (fields.some((f) => f.field_type === "soil")) {
      kpis.push(...soilKpis.daily.soil_climate);
      for (const [k, v] of Object.entries({ ...soilKpis, daily: [] })) {
        if (!availableModules.has(k)) continue;
        kpis.push(...v);
      }
    }
    if (fields.some((f) => f.field_type === "substrate")) {
      for (const [k, v] of Object.entries(substrateKpis)) {
        if (!availableModules.has(k)) continue;
        kpis.push(...v);
      }
    }
    const uniqueKpis = deduplicate(kpis, (a) => a);
    return uniqueKpis.map((kpi) => ({
      id: kpi,
      label: metrics.find((m) => m.ref === kpi)?.short_name || kpi,
    }));
  }, [metrics, fields]);

  const [pendingChanges, setPendingChanges] = useState<string[]>([]);
  const togglePendingChange = (columnId: string) => {
    if (pendingChanges.includes(columnId)) {
      setPendingChanges(pendingChanges.filter((c) => c !== columnId));
    } else {
      setPendingChanges([...pendingChanges, columnId]);
    }
  };

  const saveChanges = () => {
    toggleColumns(pendingChanges);
    setPendingChanges([]);
  };

  return (
    <div className={classes.settingsList}>
      <Divider />
      <List dense>
        {columns.map(
          (item) =>
            item && (
              <ListItem button className={classes.listItem} key={item.id}>
                <ListItemText>
                  <Typography variant="body2" noWrap={true}>
                    {item.label}
                  </Typography>
                </ListItemText>
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    color="primary"
                    onChange={() => togglePendingChange(item.id)}
                    checked={
                      isVisible(item.id) === !pendingChanges.includes(item.id)
                    }
                  />
                </ListItemSecondaryAction>
              </ListItem>
            )
        )}
      </List>

      <FormButton
        text={"Save"}
        disabled={pendingChanges.length === 0}
        variant="contained"
        color="primary"
        handleClick={() => saveChanges()}
        buttonStyles={classes.formButton}
      />
    </div>
  );
};

export default DashboardColumnSettings;
